// src/hooks/useFirebaseAuth.ts
// Owns the Firebase onAuthStateChanged subscription and wires the auth
// lifecycle to progressSync + the auth store.
//
// Mount this ONCE at the app root (inside <App>). It returns nothing — its
// side-effects update useAuthStore, which all other components subscribe to.

import { onAuthStateChanged } from 'firebase/auth';
import { onSnapshot, doc } from 'firebase/firestore';
import { useEffect, useRef } from 'react';

import { auth, db } from '../lib/firebase';
import { progressSync } from '../services/progressSync';
import { ensureAnonymousAuth } from '../services/authService';
import { useAuthStore, type UserProfile } from '../stores/useAuthStore';
import { useProgressSync } from './useProgressSync';

function parseProfileDoc(d: Record<string, unknown>): UserProfile {
  const sub = (d['subscription'] as Record<string, unknown>) ?? {};
  return {
    displayName: (d['displayName'] as string) ?? '',
    email:       (d['email']       as string) ?? '',
    phone:       (d['phone']       as string) ?? '',
    occupation:  (d['occupation']  as string) ?? '',
    institution: (d['institution'] as string) ?? '',
    avatarUrl:         (d['avatarUrl']         as string | null) ?? null,
    avatarStoragePath: (d['avatarStoragePath'] as string | null) ?? null,
    authProvider: (d['authProvider'] as 'google' | 'email' | null) ?? null,
    hasFullAccess: d['hasFullAccess'] === true,
    subscription: {
      status:      ((sub['status']  as string) ?? 'inactive') as 'active' | 'inactive' | 'expired',
      plan:        ((sub['plan']    as string) ?? null) as 'monthly' | 'three_months' | 'yearly' | null,
      startDate:   sub['startDate'] ? (sub['startDate'] as { toDate(): Date }).toDate() : null,
      endDate:     sub['endDate']   ? (sub['endDate']   as { toDate(): Date }).toDate() : null,
      isLifetime:  sub['isLifetime'] === true,
      promoCode:   (sub['promoCode'] as string | undefined) ?? undefined,
    },
    createdAt: (d['createdAt'] as string) ?? '',
  };
}

export function useFirebaseAuth(): void {
  const setUser       = useAuthStore((s) => s.setUser);
  const setAuthLoaded = useAuthStore((s) => s.setAuthLoaded);
  const setProfile    = useAuthStore((s) => s.setProfile);
  const setSyncStatus = useAuthStore((s) => s.setSyncStatus);
  const isLoggingOut  = useAuthStore((s) => s.isLoggingOut);
  const profileUnsubRef = useRef<(() => void) | null>(null);

  // ── Bind useProgressSync output → store.syncStatus ─────────────────────
  // The hook returns { isSyncing, lastSync, pendingCount, ... }.
  // We rename pendingCount → pendingItems per the handoff spec.
  const { isSyncing, lastSync, pendingCount } = useProgressSync();

  useEffect(() => {
    setSyncStatus({
      isSyncing,
      lastSync,
      pendingItems: pendingCount,
    });
  }, [isSyncing, lastSync, pendingCount, setSyncStatus]);

  // ── Firebase auth state listener ────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Always tear down any existing profile listener when auth state changes.
      profileUnsubRef.current?.();
      profileUnsubRef.current = null;

      const isAnon = firebaseUser?.isAnonymous === true;

      // No user at all — sign in anonymously so Firestore security rules
      // still allow reading the books collection (rules require auth).
      if (!firebaseUser && !isLoggingOut) {
        await ensureAnonymousAuth();
        return;
      }

      if (isAnon) {
        // Anonymous users: clear any stored real-user data, notify SW.
        setUser(null);
        setProfile(null);
        setAuthLoaded(true);
        try {
          navigator.serviceWorker?.controller?.postMessage({ type: 'AUTH_CLEAR' });
        } catch (_) {}
        return;
      }

      if (firebaseUser && !isAnon) {
        // Authenticated real user.
        setUser(firebaseUser);
        setAuthLoaded(true);

        // Real-time profile listener — fires immediately with current data and
        // again on every change (e.g. admin granting premium access).
        profileUnsubRef.current = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          (snap) => {
            if (!snap.exists()) { setProfile(null); return; }
            setProfile(parseProfileDoc(snap.data() as Record<string, unknown>));
          },
          (err) => {
            console.error('Profile snapshot error:', err);
            setProfile(null);
          }
        );

        // Enable real-time progress listeners now that we have a real uid.
        try {
          progressSync.enableRealTimeListeners();
        } catch (e) {
          console.warn('Could not enable real-time listeners:', e);
        }

        // Delay pending-sync check slightly to let Firestore connection settle.
        setTimeout(async () => {
          try {
            const pending = await progressSync.getPendingCount();
            setSyncStatus({ pendingItems: pending });
          } catch (_) {}
        }, 1_000);
      }
    });

    return () => {
      unsubscribe();
      profileUnsubRef.current?.();
      profileUnsubRef.current = null;
    };
    // isLoggingOut intentionally omitted from deps — it changes synchronously
    // during logout and we don't want to re-subscribe the listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}